import { FormEvent, useState } from "react";
import styles from "../../styles/welcome.module.css";
import { useClient } from "../../provider/ClientProvider";
import { getUserDevice } from "../../utils/helper";
import { useTranslation } from "react-i18next";

interface ClientCodeStepProps {
  onVerifyOnMobile: () => void;
}

/**
 * First stage: the code, or a hand-off to a phone.
 *
 * One field, deliberately. The code an applicant was given may identify their
 * organisation or one specific issuer within it, and they should not have to know
 * which; the server works that out and the journey continues accordingly.
 *
 * The hand-off sits alongside rather than after: someone on a desktop with no
 * camera needs to know that switching to a phone is an option before they invest
 * anything in this form. It is offered here and not on a phone, where it would
 * only send them back to where they already are.
 */
const ClientCodeStep = ({ onVerifyOnMobile }: ClientCodeStepProps) => {
  const { t } = useTranslation();
  const { lookupCode, isResolving } = useClient();
  const [code, setCode] = useState("");
  /*
   * A device question, not a width one: the point is not to offer a hand-off to a
   * phone when this already is one. A narrow desktop window is still a desktop and
   * should keep the option.
   */
  const onMobile = getUserDevice() === "MOBILE";

  const canSubmit = code.trim().length > 0 && !isResolving;

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (!canSubmit) return;
    await lookupCode(code.trim());
  }

  return (
    <>
      <form onSubmit={handleSubmit} className={styles.clientIdForm}>
        <div className={styles.field}>
          <label className={styles.clientIdLabel} htmlFor="clientCode">
            {t("clientid.code.label")}
          </label>
          <input
            id="clientCode"
            name="clientCode"
            className={styles.clientIdInput}
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder={t("clientid.code.placeholder")}
            autoComplete="off"
            autoCapitalize="characters"
            autoFocus
            spellCheck={false}
            disabled={isResolving}
          />
          <span className={styles.clientIdHint}>{t("clientid.code.hint")}</span>
        </div>

        <button
          type="submit"
          className={canSubmit ? styles.button_primary : styles.button_primary_disabled}
          disabled={!canSubmit}
        >
          {isResolving ? t("clientid.button.checking") : t("clientid.button.continue")}
        </button>
      </form>

      {!onMobile && (
        <>
          <div className={styles.orDivider}>{t("landing.or")}</div>
          <button type="button" className={styles.button_secondary} onClick={onVerifyOnMobile}>
            {t("button.verify")}
          </button>
        </>
      )}
    </>
  );
};

export default ClientCodeStep;
